import { useField } from '@kizi/comparator-core';
import { Div, P } from '@kizi/core-components';
import { Slider, SliderControl, CurrentSlide } from '@kizi/shared-components';
import React, { useMemo } from 'react';
import { Form, FormSubmit, FormProps, FieldViewer } from './Form';

interface FormWidgetProps extends Omit<FormProps, 'children'> {
  editModeLayout: React.ReactNode;
  editModeFooter?: React.ReactNode;
  viewModeFooter?: React.ReactNode;
  viewModeLayout: React.ReactNode[];
  initialMode?: Mode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

enum Mode {
  VIEW = 0,
  EDIT = 1,
}

export function addHouseNumberAdditionAfterFirstNumber(str, addition) {
  // use regular expression to find the first occurrence of a number
  const inputString = removeLetterAfterFirstNumber(str);
  const match = inputString.match(/\d+/);

  if (match) {
    // find the position of the matched number in the input string
    const numberIndex = inputString.indexOf(match[0]);
    const result =
      inputString.slice(0, numberIndex + match[0].length) +
      ' ' +
      addition.toUpperCase() +
      inputString.slice(numberIndex + match[0].length);
    return result;
  }
  // if no number is found, return the original string
  return inputString;
}

export function removeLetterAfterFirstNumber(inputString) {
  // use regular expression to find the first occurrence of a number
  const match = inputString.match(/\d+/);
  if (match) {
    // use regular expression to find the first letter after the first number, if it exists
    const letterMatch = inputString.match(new RegExp(`${match[0]}([a-zA-Z])`));
    if (letterMatch) {
      // if a letter is found, remove it using the `replace` method
      const letterToRemove = letterMatch[1];
      return inputString.replace(
        new RegExp(`${match[0]}${letterToRemove}`),
        match[0]
      );
    }
  }
  // if no number or no letter after the first number is found, return the original string
  return inputString;
}

type PersonalDataFieldViewerProps = { name: string };

export const PersonalDataFieldViewer: React.FC<PersonalDataFieldViewerProps> =
  ({ name }) => {
    const houseNumberAdditionField = useField(
      'getUserInput.housenumberaddition'
    );
    const houseNumberAddition = houseNumberAdditionField?.value;

    return (
      <FieldViewer name={name}>
        {({ active, value, label, meta }) => {
          if (active === false) return null;

          if (name === 'fullAddress') {
            return (
              <P variant="footnoteRegular" mt={3}>
                {label ? label + ':' : null}{' '}
                {houseNumberAddition
                  ? addHouseNumberAdditionAfterFirstNumber(
                      value,
                      houseNumberAddition
                    )
                  : value}{' '}
                {meta?.unit ?? null}
              </P>
            );
          }

          return (
            <P variant="footnoteRegular" mt={3}>
              {label ? label + ':' : null} {meta?.prefix ?? null} {value} {meta?.unit ?? null}
            </P>
          );
        }}
      </FieldViewer>
    );
  };

interface ViewModeProps {
  footer?: React.ReactNode;
  viewModeLayout: React.ReactNode[];
}

const defaultViewModeFooter = (
  <SliderControl
    goto={Mode.EDIT}
    variant="transparent"
    mt={3}
    fontSize={[2, 3]}
  >
    Wijzig
  </SliderControl>
);

const ViewMode: React.FC<React.PropsWithChildren<ViewModeProps>> = React.memo(
  ({ viewModeLayout, footer = defaultViewModeFooter }) => {
    const fieldViewers = viewModeLayout.map((f, i) =>
      typeof f === 'string' ? <PersonalDataFieldViewer key={i} name={f} /> : f
    );

    return (
      <>
        {fieldViewers}
        {footer}
      </>
    );
  }
);
interface EditModeProps {
  layout: React.ReactNode;
  footer?: React.ReactNode;
}

const defaultEditModeFooter = (
  <Div
    mt={3}
    display="flex"
    justifyContent="space-between"
    width={['50%', '100%', '100%']}
  >
    <FormSubmit variant="primary" fontSize={[2, 3]} mr={[2, 0, 0]}>
      Opslaan
    </FormSubmit>
    <SliderControl goto={Mode.VIEW} variant="transparent" fontSize={[2, 3]}>
      Annuleren
    </SliderControl>
  </Div>
);

const EditMode: React.FC<EditModeProps> = ({
  layout,
  footer = defaultEditModeFooter,
}) => {
  return (
    <>
      {layout}
      {footer}
    </>
  );
};
export const FormWidget: React.FC<FormWidgetProps> = ({
  fields,
  editModeLayout,
  editModeFooter,
  viewModeFooter,
  viewModeLayout,
  initialMode = Mode.VIEW,
  header = null,
  footer = null,
  ...formProps
}) => {
  const viewModeElement = useMemo(() => {
    return <ViewMode viewModeLayout={viewModeLayout} footer={viewModeFooter} />;
  }, [fields, viewModeFooter, viewModeLayout, formProps]);
  const editModeElement = useMemo(() => {
    return <EditMode layout={editModeLayout} footer={editModeFooter} />;
  }, [fields, editModeLayout, editModeFooter]);
  const sliderElements = useMemo(
    () => [viewModeElement, editModeElement],
    [viewModeElement, editModeElement]
  );
  return (
    <Form fields={fields} {...formProps}>
      <Slider initialCurrentPage={initialMode} elements={sliderElements}>
        {header}
        <CurrentSlide />
        {footer}
      </Slider>
    </Form>
  );
};
